import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { formatHoursDurationCompact } from "@src/appV2/redesign/utils/formatHoursDurationCompact";

import { type TimesheetEntry } from "../hooks/useTimesheetEntry";
import { calculateTimesheetWorkedHours } from "../utils/timesheetFormatUtils";

interface TimesheetTotalsFooterProps {
  displayTimesheet: TimesheetEntry;
  /**
   * Pass the original shift timesheet (hcp_clock_in/out) to show a Verified/Pending
   * hours breakdown when the worker is claiming more time than recorded.
   */
  verifiedTimesheet?: TimesheetEntry;
  /** When true, shows "Verified hours" breakdown instead of "Total". Gate behind isMissedPunchRequestEnabledV2. */
  shouldShowVerifiedHoursLabel?: boolean;
  /**
   * Options forwarded to formatHoursDurationCompact.
   * Defaults to { areLeadingZeroesAllowed: false, omitZeroMinutes: true } (compact/editable style).
   * Pass { areLeadingZeroesAllowed: true, omitZeroMinutes: false } to restore the read-only style
   * that shows "8h 00m" rather than "8h".
   */
  formatOptions?: { areLeadingZeroesAllowed?: boolean; omitZeroMinutes?: boolean };
}

export function TimesheetTotalsFooter(props: TimesheetTotalsFooterProps) {
  const {
    displayTimesheet,
    verifiedTimesheet,
    shouldShowVerifiedHoursLabel = false,
    formatOptions,
  } = props;

  const adjustedHours = calculateTimesheetWorkedHours(displayTimesheet);
  const verifiedHours = isDefined(verifiedTimesheet)
    ? calculateTimesheetWorkedHours(verifiedTimesheet)
    : undefined;
  const pendingHours =
    isDefined(verifiedHours) && adjustedHours > verifiedHours
      ? adjustedHours - verifiedHours
      : undefined;

  if (shouldShowVerifiedHoursLabel && isDefined(verifiedHours)) {
    return (
      <Stack component="dl" spacing={2} sx={{ margin: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box component="dt">
            <Text bold sx={{ color: (theme) => theme.palette.text.tertiary }}>
              Verified hours
            </Text>
          </Box>
          <Box component="dd" sx={{ margin: 0 }}>
            <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
              {formatWorkedHours(verifiedHours, formatOptions)}
            </Text>
          </Box>
        </Stack>
        {isDefined(pendingHours) && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box component="dt">
              <Text bold sx={{ color: (theme) => theme.palette.text.tertiary }}>
                Pending approval
              </Text>
            </Box>
            <Box component="dd" sx={{ margin: 0 }}>
              <Text bold sx={{ color: (theme) => theme.palette.success.main }}>
                +{formatWorkedHours(pendingHours, formatOptions)}
              </Text>
            </Box>
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Text bold variant="h5" sx={{ color: (theme) => theme.palette.text.tertiary }}>
        Total
      </Text>
      <Text bold variant="h5" sx={{ color: (theme) => theme.palette.success.main }}>
        {formatWorkedHours(adjustedHours, formatOptions)}
      </Text>
    </Stack>
  );
}

function formatWorkedHours(
  hours: number,
  options?: { areLeadingZeroesAllowed?: boolean; omitZeroMinutes?: boolean }
): string {
  return formatHoursDurationCompact(
    hours,
    options ?? { areLeadingZeroesAllowed: false, omitZeroMinutes: true }
  );
}
